<template>
  <div class="">

    <Head />

    <div class="main scheduleList">

      <AccesskeyLink target="accesskey_c" accesskey="c" :aria-label="$t('跳到中央內容區塊')">
        {{ $t(':::') }}
      </AccesskeyLink>

      <!-- 毛毛蟲 -->
      <nav aria-label="breadcrumb" class="breadcrumb_box">
        <div class="container">
          <ol class="breadcrumb">
            <li class="breadcrumb-item"><router-link to="/" :title="$t('首頁')">{{ $t('首頁') }}</router-link></li>
            <li class="breadcrumb-item"><router-link to="/ScheduleLogin" :title="$t('員/志工排班')">{{ $t('員/志工排班') }}</router-link></li>
            <li class="breadcrumb-item active" aria-current="page">{{ $t('排班列表') }}</li>
          </ol>
        </div>
      </nav>

      <div class="scheduleList_box">
        <div class="container">

          <div class="title_box">
            <h2>{{ $t('排班列表') }}</h2>
            <div class="user_info">
              <span class="user_name">{{ userName }} ({{ accountTypeName }})</span>
              <button class="logout_btn" @click="logout">{{ $t('登出') }}</button>
            </div>
          </div>

          <!-- 打卡資訊區 (只有志工且在允許的IP才顯示) -->
          <div v-if="accountType === 'Volunteer' && isInAllowedIp" class="checkin_info_box">
            <div class="date_time_box">
              <div class="date_display">{{ currentDateDisplay }}</div>
              <div class="time_display">{{ currentTimeDisplay }}</div>
            </div>
            <div class="checkin_status_box">
              <div v-if="todayShiftType === null" class="status_message">
                <span class="badge badge_grey">{{ $t('今日無勤務') }}</span>
              </div>
              <div v-else class="checkin_buttons_wrapper">
                <!-- 上午打卡按鈕 -->
                <div v-if="needMorningCheckIn" class="checkin_btn_group">
                  <button class="checkin_btn" :class="getMorningButtonClass()" :disabled="isMorningButtonDisabled()"
                    @click="handleMorningCheckIn()">
                    {{ getMorningButtonText() }}
                  </button>
                </div>
                <!-- 下午打卡按鈕 -->
                <div v-if="needAfternoonCheckIn" class="checkin_btn_group">
                  <button class="checkin_btn" :class="getAfternoonButtonClass()" :disabled="isAfternoonButtonDisabled()"
                    @click="handleAfternoonCheckIn()">
                    {{ getAfternoonButtonText() }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 新聞列表區塊 -->
          <div v-if="newsList.length > 0" class="news_section">
            <div class="news_title">
              <h3>{{ accountType === 'Employee' ? $t('員工公告') : $t('志工公告') }}</h3>
            </div>
            <ul class="news_list">
              <li v-for="item in newsList" :key="item.id">
                <a v-if="item.isExternal" class="news" :href="item.to" :target="item.target"
                  :title="item.title + $t('（另開新視窗）')">
                  <p class="time">{{ item.time }}</p>
                  <p class="title">{{ item.title }}</p>
                </a>
                <router-link v-else class="news" :to="item.to" :title="item.title + $t('（另開新視窗）')" target="_blank">
                  <p class="time">{{ item.time }}</p>
                  <p class="title">{{ item.title }}</p>
                </router-link>
              </li>
            </ul>
          </div>

          <!-- 載入中 -->
          <div v-if="isLoading" class="loading_box">
            <p>{{ $t('載入中...') }}</p>
          </div>

          <!-- 錯誤訊息 -->
          <div v-else-if="errorMessage" class="alert alert-danger" role="alert">
            {{ errorMessage }}
          </div>

          <!-- 排班列表 -->
          <div v-else-if="scheduleList.length > 0" class="schedule_list">
            <div v-for="schedule in scheduleList" :key="schedule.csId" class="schedule_item">
              <div class="schedule_info">
                <h3>{{ schedule.scheduleName }}</h3>
                <div class="schedule_detail">
                  <p><strong>{{ $t('排班期間') }}:</strong> {{ formatDate(schedule.startDate) }} ~ {{ formatDate(schedule.endDate) }}
                  </p>

                  <!-- 狀態顯示 -->
                  <div class="schedule_status">
                    <span v-if="schedule.status === 'notOpen'" class="badge badge_grey">{{ $t('未開放排班') }}</span>
                    <span v-else-if="schedule.status === 'open'" class="badge badge_green">{{ $t('排班中') }}</span>
                    <span v-else-if="schedule.status === 'closed'" class="badge badge_red">{{ $t('排班已截止') }}</span>
                  </div>

                  <!-- 開放時間 -->
                  <p v-if="schedule.status === 'notOpen'" class="open_time">
                    <strong>{{ $t('開放排班時間') }}:</strong> {{ formatDateTime(schedule.openSchedulingStartTime) }} ~ {{
                      formatDateTime(schedule.openSchedulingEndTime) }}
                  </p>

                  <!-- 排班狀態 -->
                  <div class="submit_status">
                    <span v-if="schedule.isSubmitted" class="badge badge_blue">{{ $t('已送出') }}</span>
                    <span v-else-if="schedule.hasDraft" class="badge badge_warning">{{ $t('暫存中') }}</span>
                    <span v-else class="badge badge_orange">{{ $t('未送出') }}</span>
                  </div>

                  <!-- 時數資訊 (只有志工且已送出才顯示) -->
                  <div v-if="accountType === 'Volunteer' && schedule.isSubmitted">
                    <p><strong>{{ $t('預計時數') }}:</strong> {{ schedule.expectedHours || 0 }} {{ $t('小時') }}</p>
                    <p><strong>{{ $t('實際時數') }}:</strong> {{ schedule.actualHours || 0 }} {{ $t('小時') }}</p>
                  </div>
                </div>
              </div>

              <div class="schedule_actions">
                <!-- 根據狀態和帳號類型顯示按鈕 -->
                <button v-if="schedule.status === 'open'" class="btn btn_primary" @click="startScheduling(schedule)">
                  {{ schedule.isSubmitted ? $t('檢視排班') : $t('開始排班') }}
                </button>
                <button v-else-if="schedule.status === 'closed' && schedule.isSubmitted" class="btn btn_secondary"
                  @click="startScheduling(schedule)">
                  {{ $t('檢視排班') }}
                </button>
                <button v-else class="btn btn_disabled" disabled>
                  {{ $t('尚未開放') }}
                </button>
              </div>
            </div>
          </div>

          <!-- 無資料 -->
          <div v-else class="no_data">
            <p>{{ $t('目前沒有可排班的項目') }}</p>
          </div>

        </div>
      </div>

    </div>

    <Footer />

  </div>
</template>

<script>
import Head from '@/components/Head.vue'
import Footer from '@/components/Footer.vue';
import AccesskeyLink from '@/views/AccesskeyLink.vue'
import Swal from 'sweetalert2';

export default {
  name: 'ScheduleList',
  components: {
    Head,
    Footer,
    AccesskeyLink,
  },

  data() {
    return {
      // 使用者資訊
      userName: '',
      accountType: '',
      csvId: null,

      // 排班列表
      scheduleList: [],

      // 打卡相關
      isInAllowedIp: false,
      currentDateDisplay: '',
      currentTimeDisplay: '',
      todayShiftType: null, // 今日班別類型: 'morning', 'afternoon', 'fullday', null
      currentCsId: null, // 當前排班ID
      clockTimer: null, // 時鐘計時器

      // 打卡狀態
      morningCheckInTime: null,
      morningCheckOutTime: null,
      afternoonCheckInTime: null,
      afternoonCheckOutTime: null,

      // 新聞相關
      newsList: [],
      newsLoading: false,

      // 狀態
      isLoading: true,
      errorMessage: '',
    };
  },

  computed: {
    accountTypeName() {
      return this.accountType === 'Employee' ? this.$t('員工') : this.$t('志工');
    },

    // 是否需要顯示上午打卡按鈕
    needMorningCheckIn() {
      return this.todayShiftType === 'morning' || this.todayShiftType === 'fullday';
    },

    // 是否需要顯示下午打卡按鈕
    needAfternoonCheckIn() {
      return this.todayShiftType === 'afternoon' || this.todayShiftType === 'fullday';
    },
  },

  methods: {

    // 轉換前端語系名稱為後端格式
    convertLocaleForBackend(frontendLocale) {
      const localeMap = {
        'zh-TW': 'zh-tw',
        'en': 'en-us',
        'ja': 'ja-jp'
      };
      return localeMap[frontendLocale] || 'zh-tw';
    },

    // 檢查登入狀態
    checkLogin() {
      const token = sessionStorage.getItem('scheduleToken');
      const csvId = sessionStorage.getItem('scheduleCSVId');
      const name = sessionStorage.getItem('scheduleName');
      const accountType = sessionStorage.getItem('scheduleAccountType');

      if (!token || !csvId) {
        this.$router.push({ name: 'ScheduleLogin' });
        return false;
      }

      this.userName = name || '';
      this.accountType = accountType || '';
      this.csvId = parseInt(csvId);
      return true;
    },

    // 載入排班列表
    async loadScheduleList() {
      if (!this.checkLogin()) return;

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaStaffVolunteer/GetScheduleList`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            CSVId: this.csvId,
          }),
        });

        const data = await response.json();

        if (data.ok) {
          // 處理每個排班項目的狀態
          const now = new Date();
          this.scheduleList = data.list.map(item => {
            const openStart = item.openSchedulingStartTime ? new Date(item.openSchedulingStartTime) : null;
            const openEnd = item.openSchedulingEndTime ? new Date(item.openSchedulingEndTime) : null;
            let status = 'notOpen';
            if (openStart && openEnd) {
              if (now < openStart) {
                status = 'notOpen';
              } else if (now >= openStart && now <= openEnd) {
                status = 'open';
              } else {
                status = 'closed';
              }
            }
            
            const clockStart = item.startDate ? new Date(item.startDate) : null;
            const clockEnd = item.endDate ? new Date(item.endDate) : null;
            let clockStatus = 'notOpen';
            if (clockStart && clockEnd) {
              if (now < clockStart) {
                clockStatus = 'notOpen';
              } else if (now >= clockStart && now <= clockEnd) {
                clockStatus = 'open';
              } else {
                clockStatus = 'closed';
              }
            }

            return {
              ...item,
              status: status,
              clockStatus: clockStatus,
            };
          });
        } else {
          this.errorMessage = data.message || this.$t('載入排班列表失敗');
        }
      } catch (error) {
        console.error('載入排班列表錯誤:', error);
        this.errorMessage = this.$t('載入排班列表失敗，請稍後再試');
      } finally {
        this.isLoading = false;
      }
    },

    // 開始排班
    startScheduling(schedule) {
      // 根據帳號類型導向不同頁面
      if (this.accountType === 'Employee') {
        this.$router.push({
          name: 'ShiftSchedule',
          params: {
            csId: schedule.csId,
          },
        });
      } else {
        this.$router.push({
          name: 'VolunteerSchedule',
          params: {
            csId: schedule.csId,
          },
        });
      }
    },

    // 登出
    logout() {
      // 清除登入資訊
      sessionStorage.removeItem('scheduleToken');
      sessionStorage.removeItem('scheduleCSVId');
      sessionStorage.removeItem('scheduleEmail');
      sessionStorage.removeItem('scheduleName');
      sessionStorage.removeItem('scheduleAccountType');
      sessionStorage.removeItem('scheduleLoginTime');

      // 跳轉到登入頁
      this.$router.push({ name: 'ScheduleLogin' });
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    },

    // 格式化日期時間
    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },

    // 格式化時間
    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },

    // 檢查當前IP是否允許打卡
    async checkCurrentIp() {
      if (this.accountType !== 'Volunteer') return;

      try {
        const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaScheduling/CheckCurrentIp`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.ok) {
          this.isInAllowedIp = data.isAllowed;

          // 如果在允許的IP，則開始時鐘並檢查打卡狀態
          if (this.isInAllowedIp) {
            this.startClock();
            await this.checkTodayCheckInStatus();
          }
        }
      } catch (error) {
        console.error('檢查IP失敗:', error);
      }
    },

    // 開始時鐘
    startClock() {
      this.updateClock();
      this.clockTimer = setInterval(() => {
        this.updateClock();
      }, 1000);
    },

    // 更新時鐘顯示
    updateClock() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');

      this.currentDateDisplay = `${year}${this.$t('年')}${month}${this.$t('月')}${day}${this.$t('日')}`;
      this.currentTimeDisplay = `${hours}:${minutes}:${seconds}`;
    },

    // 檢查今日打卡狀態
    async checkTodayCheckInStatus() {
      if (!this.currentCsId) return;

      try {
        const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaStaffVolunteer/GetCheckInStatus`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            csvId: this.csvId,
            csId: this.currentCsId,
          }),
        });

        const data = await response.json();
        if (data.ok) {
          // 找今天的記錄（API 回傳的時間已經是 GMT+8，直接比對日期字串）
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          const todayRecord = data.checkInStatus.find(record => {
            const recordDate = record.scheduleDate.split('T')[0];
            return recordDate === todayStr;
          });

          if (todayRecord) {
            this.todayShiftType = todayRecord.shiftType;
            this.morningCheckInTime = todayRecord.morningCheckInTime;
            this.morningCheckOutTime = todayRecord.morningCheckOutTime;
            this.afternoonCheckInTime = todayRecord.afternoonCheckInTime;
            this.afternoonCheckOutTime = todayRecord.afternoonCheckOutTime;
          } else {
            // 今天沒有排班
            this.todayShiftType = null;
          }
        }
      } catch (error) {
        console.error('檢查打卡狀態失敗:', error);
      }
    },

    // ========== 上午打卡按鈕相關 ==========

    // 取得上午打卡按鈕文字
    getMorningButtonText() {
      if (!this.morningCheckInTime) {
        return this.$t('上午打卡');
      } else if (!this.morningCheckOutTime) {
        return this.$t('上午打卡(下班)');
      } else {
        return this.$t('上午打卡(完成)');
      }
    },

    // 取得上午打卡按鈕樣式
    getMorningButtonClass() {
      if (!this.morningCheckInTime) {
        return 'checkin_in';
      } else if (!this.morningCheckOutTime) {
        return 'checkin_out';
      } else {
        return 'checkin_done';
      }
    },

    // 上午打卡按鈕是否禁用
    isMorningButtonDisabled() {
      return this.morningCheckInTime && this.morningCheckOutTime;
    },

    // 處理上午打卡
    async handleMorningCheckIn() {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      const noon = 12 * 60; // 12:00

      if (!this.morningCheckInTime) {
        // 上班打卡
        if (currentTime >= noon) {
          await Swal.fire({
            icon: 'warning',
            title: this.$t('已超過打卡時間'),
            text: this.$t('上午打卡時間已過')
          });
          return;
        }

        await this.doCheckIn('morning', 'in');
      } else if (!this.morningCheckOutTime) {
        // 下班打卡
        await this.doCheckOut('morning', 'out');
      }
    },

    // ========== 下午打卡按鈕相關 ==========

    // 取得下午打卡按鈕文字
    getAfternoonButtonText() {
      if (!this.afternoonCheckInTime) {
        return this.$t('下午打卡');
      } else if (!this.afternoonCheckOutTime) {
        return this.$t('下午打卡(下班)');
      } else {
        return this.$t('下午打卡(完成)');
      }
    },

    // 取得下午打卡按鈕樣式
    getAfternoonButtonClass() {
      if (!this.afternoonCheckInTime) {
        return 'checkin_in';
      } else if (!this.afternoonCheckOutTime) {
        return 'checkin_out';
      } else {
        return 'checkin_done';
      }
    },

    // 下午打卡按鈕是否禁用
    isAfternoonButtonDisabled() {
      return this.afternoonCheckInTime && this.afternoonCheckOutTime;
    },

    // 處理下午打卡
    async handleAfternoonCheckIn() {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      const noon = 12 * 60; // 12:00
      const endTime = 16 * 60; // 16:00

      if (!this.afternoonCheckInTime) {
        // 上班打卡
        if (currentTime < noon) {
          await Swal.fire({
            icon: 'warning',
            title: this.$t('未達可打卡時間'),
            text: this.$t('下午打卡時間尚未到')
          });
          return;
        }

        if (currentTime >= endTime) {
          await Swal.fire({
            icon: 'warning',
            title: this.$t('已超過打卡時間'),
            text: this.$t('下午打卡時間已過')
          });
          return;
        }

        await this.doCheckIn('afternoon', 'in');
      } else if (!this.afternoonCheckOutTime) {
        // 下班打卡
        await this.doCheckOut('afternoon', 'out');
      }
    },

    // 執行打卡（上班）
    async doCheckIn(shiftPeriod, checkType) {
      try {
        const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaStaffVolunteer/CheckIn`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            csvId: this.csvId,
            csId: this.currentCsId,
            checkInDate: new Date().toISOString().split('T')[0],
            shiftPeriod: shiftPeriod,
            checkType: checkType
          })
        });

        const result = await response.json();
        if (result.ok) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('打卡成功'),
            showConfirmButton: false,
            timer: 1500
          });

          // 重新載入打卡狀態
          await this.checkTodayCheckInStatus();
        } else {
          await Swal.fire({
            icon: 'error',
            title: this.$t('打卡失敗'),
            text: result.message || this.$t('打卡失敗')
          });
        }
      } catch (error) {
        console.error('打卡失敗:', error);
        await Swal.fire({
          icon: 'error',
          title: this.$t('打卡失敗'),
          text: this.$t('打卡失敗，請稍後再試')
        });
      }
    },

    // 執行打卡（下班）
    async doCheckOut(shiftPeriod, checkType) {
      // 計算已執勤時間
      let workedTimeText = '';
      const checkInTime = shiftPeriod === 'morning' ? this.morningCheckInTime : this.afternoonCheckInTime;
      
      if (checkInTime) {
        const checkInDate = new Date(checkInTime);
        const now = new Date();
        const diffMs = now - checkInDate;
        const diffMinutes = Math.floor(diffMs / 1000 / 60);
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        
        if (hours > 0) {
          workedTimeText = `<br><span style="color: #007bff; font-weight: 600;">${this.$t('已執勤：')}${hours} ${this.$t('小時')} ${minutes} ${this.$t('分鐘')}</span>`;
        } else {
          workedTimeText = `<br><span style="color: #007bff; font-weight: 600;">${this.$t('已執勤：')}${minutes} ${this.$t('分鐘')}</span>`;
        }
      }
      
      // 先詢問確認
      const result = await Swal.fire({
        icon: 'question',
        title: this.$t('確認下班'),
        html: `${this.$t('確定要下班打卡嗎？')}<br>${this.$t('系統將計算您的工作時數')}${workedTimeText}`,
        showCancelButton: true,
        confirmButtonText: this.$t('確認下班'),
        cancelButtonText: this.$t('取消')
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaStaffVolunteer/CheckIn`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            csvId: this.csvId,
            csId: this.currentCsId,
            checkInDate: new Date().toISOString().split('T')[0],
            shiftPeriod: shiftPeriod,
            checkType: checkType
          })
        });

        const apiResult = await response.json();
        if (apiResult.ok) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('下班打卡成功'),
            html: apiResult.message,
            timer: 2000,
            showConfirmButton: false
          });

          // 重新載入打卡狀態
          await this.checkTodayCheckInStatus();
        } else {
          await Swal.fire({
            icon: 'error',
            title: this.$t('打卡失敗'),
            text: apiResult.message || this.$t('打卡失敗')
          });
        }
      } catch (error) {
        console.error('打卡失敗:', error);
        await Swal.fire({
          icon: 'error',
          title: this.$t('打卡失敗'),
          text: this.$t('打卡失敗，請稍後再試')
        });
      }
    },

    // 載入新聞資料
    async loadNewsData() {
      if (!this.accountType) return;

      this.newsLoading = true;

      try {
        const backendLocale = this.convertLocaleForBackend(this.$i18n.locale);
        
        // 取得 Scheduling 的 LayoutTab
        const layoutUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/Frontend/GetLayout?TabKey=Scheduling&locale=${backendLocale}`;
        const layoutResponse = await fetch(layoutUrl);

        if (!layoutResponse.ok) {
          console.warn('無法取得 Scheduling 版面配置，清空新聞資料');
          this.newsList = [];
          return;
        }

        const layout = await layoutResponse.json();

        if (!layout.layoutItems || !Array.isArray(layout.layoutItems)) {
          console.warn('Scheduling 版面配置中沒有 layoutItems，清空新聞資料');
          this.newsList = [];
          return;
        }

        // 根據帳號類型決定要找哪個 ItemKey
        const targetItemKey = this.accountType === 'Employee' ? 'StaffNews' : 'VolunteerNews';

        // 找到對應的 LayoutItem
        const layoutItem = layout.layoutItems.find(item => item.itemKey === targetItemKey);

        if (!layoutItem || !layoutItem.referenceId) {
          console.warn(`找不到 ${targetItemKey} 對應的 LayoutItem 或 referenceId，清空新聞資料`);
          this.newsList = [];
          return;
        }

        // 取得該分類的文章資料
        const categoryUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/Frontend/GetCategoryData?CId=${layoutItem.referenceId}&IncludePost=true&locale=${backendLocale}`;
        const categoryResponse = await fetch(categoryUrl);

        if (!categoryResponse.ok) {
          console.warn('無法取得分類資料，清空新聞資料');
          this.newsList = [];
          return;
        }

        const category = await categoryResponse.json();

        if (category.Posts && Array.isArray(category.Posts)) {
          // 只取前 5 篇文章
          this.newsList = category.Posts.slice(0, 5).map(post => ({
            id: post.pId,
            time: this.formatDate(post.createTime),
            title: post.title,
            to: post.type === 2 ? post.url : `/Post/${post.pId}`,
            isExternal: post.type === 2,
            target: '_blank'
          }));
        } else {
          this.newsList = [];
        }
      } catch (error) {
        console.error('載入新聞資料失敗:', error);
        this.newsList = [];
      } finally {
        this.newsLoading = false;
      }
    },
  },

  async mounted() {
    await this.loadScheduleList();

    // 如果是志工，檢查IP並啟動打卡功能
    if (this.accountType === 'Volunteer' && this.scheduleList.length > 0) {
      // 取得第一個排班中的班表ID
      const openSchedule = this.scheduleList.find(s => s.clockStatus === 'open');
      if (openSchedule) {
        this.currentCsId = openSchedule.csId;
        await this.checkCurrentIp();
      }
    }

    // 載入新聞資料
    await this.loadNewsData();
  },

  beforeDestroy() {
    // 清除計時器
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
    }
  },
}

</script>

<style scoped>
.scheduleList_box {
  padding: 40px 0;
  min-height: 500px;
}

.checkin_info_box {
  background: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.date_time_box {
  color: #333;
}

.date_display {
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 8px;
}

.time_display {
  font-size: 36px;
  font-weight: bold;
  font-family: 'Courier New', monospace;
}

.checkin_status_box {
  display: flex;
  align-items: center;
  gap: 15px;
}

.checkin_buttons_wrapper {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.checkin_btn_group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.status_message,
.status_action,
.status_checked {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkin_btn {
  padding: 12px 30px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.3s;
  white-space: nowrap;
  min-width: 140px;
}

.checkin_btn.checkin_in {
  background-color: #28a745;
  color: white;
  border-color: #28a745;
}

.checkin_btn.checkin_in:hover:not(:disabled) {
  background-color: #218838;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.checkin_btn.checkin_out {
  background-color: #fd7e14;
  color: white;
  border-color: #fd7e14;
}

.checkin_btn.checkin_out:hover:not(:disabled) {
  background-color: #e8670f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(253, 126, 20, 0.3);
}

.checkin_btn.checkin_done {
  background-color: #9E9E9E;
  color: white;
  border-color: #9E9E9E;
  cursor: not-allowed;
}

.checkin_btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.checkin_time {
  color: #495057;
  font-size: 18px;
  font-weight: 500;
}

.badge_grey,
.badge_green {
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
}

.badge_grey {
  background-color: #6c757d;
  color: white;
}

.badge_green {
  background-color: #28a745;
  color: white;
}

.title_box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.title_box h2 {
  margin: 0;
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.user_info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user_name {
  font-size: 16px;
  color: #666;
}

.logout_btn {
  padding: 8px 20px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.logout_btn:hover {
  background-color: #c82333;
}

.loading_box {
  text-align: center;
  padding: 50px 0;
  font-size: 18px;
  color: #666;
}

.schedule_list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.schedule_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s;
}

.schedule_item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.schedule_info {
  flex: 1;
}

.schedule_info h3 {
  margin: 0 0 15px 0;
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.schedule_detail {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule_detail p {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.schedule_status,
.submit_status {
  display: flex;
  gap: 10px;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.badge_grey {
  background-color: #6c757d;
  color: white;
}

.badge_green {
  background-color: #28a745;
  color: white;
}

.badge_red {
  background-color: #dc3545;
  color: white;
}

.badge_blue {
  background-color: #007bff;
  color: white;
}

.badge_orange {
  background-color: #fd7e14;
  color: white;
}

.badge_warning {
  background-color: #ffc107;
  color: #333;
}

.open_time {
  font-size: 13px;
  color: #888;
}

.hours_info {
  margin-top: 10px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.hours_info p {
  margin: 5px 0 !important;
  font-size: 14px !important;
  color: #495057 !important;
}

.schedule_actions {
  margin-left: 20px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s;
}

.btn_primary {
  background-color: #007bff;
  color: white;
}

.btn_primary:hover {
  background-color: #0056b3;
}

.btn_secondary {
  background-color: #6c757d;
  color: white;
}

.btn_secondary:hover {
  background-color: #545b62;
}

.btn_disabled {
  background-color: #e0e0e0;
  color: #999;
  cursor: not-allowed;
}

.no_data {
  text-align: center;
  padding: 80px 0;
  font-size: 18px;
  color: #999;
}

/* 新聞列表樣式 */
.news_section {
  margin-bottom: 40px;
  padding: 25px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.news_title {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #007bff;
}

.news_title h3 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.news_list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.news_list li {
  border-bottom: 1px solid #f0f0f0;
}

.news_list li:last-child {
  border-bottom: none;
}

.news_list .news {
  display: flex;
  align-items: center;
  padding: 15px 10px;
  text-decoration: none;
  transition: background-color 0.3s;
}

.news_list .news:hover {
  background-color: #f8f9fa;
}

.news_list .news .time {
  flex-shrink: 0;
  margin: 0 20px 0 0;
  font-size: 14px;
  color: #666;
  min-width: 100px;
}

.news_list .news .title {
  flex: 1;
  margin: 0;
  font-size: 15px;
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news_list .news:hover .title {
  color: #007bff;
}

@media (max-width: 768px) {
  .title_box {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .schedule_item {
    flex-direction: column;
    align-items: stretch;
  }

  .schedule_actions {
    margin-left: 0;
    margin-top: 15px;
  }

  .btn {
    width: 100%;
  }
}
</style>
